import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Info } from 'lucide-react';

import { Button } from 'components/Button';
import { FormRow, Input } from 'components/Form';
import Validator from 'components/Validator';
import GeneralError from 'components/GeneralError';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction } from 'actions/workflow';
import { cryptoAmountMask } from 'utils/masks';

import schema from './schema';

const ConfirmMicroDepositPage = ({
  navigate,
  searchParams,
  dispatch,
  validator: { onChangeHandler, values, errors, validate },
}) => {
  useWorkflowGuard(searchParams, navigate);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const customerAddress = get(checkinData, 'customerAddress');
  const decimalPlaces = get(checkinData, 'decimalPlaces') || 8;
  const amountMask = useMemo(() => cryptoAmountMask(decimalPlaces), [decimalPlaces]);
  const [generalError, setGeneralError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validate(schema(decimalPlaces)).isValid) return;

    const { amount } = values;

    setIsSubmitting(true);
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: { amount },
      success: (res) => {
        const nextUrl = get(res, 'data.url');
        if (nextUrl) navigate(nextUrl);
        setIsSubmitting(false);
      },
      fail: handleFail,
    }));
  };

  return (
    <div>
      <div className="border border-slate-200 rounded-lg p-4 mb-6">
        <p className="text-xs uppercase font-bold text-slate-500 mb-2">Micro-deposit sent to</p>
        <p className="text-sm text-slate-800 break-all">{customerAddress}</p>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-slate-800 mb-1">Why we do this</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              We sent a small micro-deposit to your wallet to confirm you control the address before we send your full collateral. Check your wallet, then enter the exact amount you received below.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-xs uppercase font-bold text-slate-500 mb-1">
          Micro-deposit amount received <span className="text-red-500">*</span>
        </label>
        <p className="text-sm text-slate-600 mb-3">
          Enter the exact BTC amount that arrived in your wallet
        </p>
        <Input
          label=""
          mask={amountMask}
          inputClassName="font-numeric"
          placeholder={`0.${'0'.repeat(decimalPlaces)}`}
          value={values.amount || ''}
          error={errors.amount}
          onChange={(e) => onChangeHandler('amount', e.target.value)}
        />
        <p className="text-sm text-slate-600 mt-3">
          Enter all {decimalPlaces} decimal places exactly as shown in your wallet. Do not round.
        </p>
      </div>

      <GeneralError error={generalError} />

      <FormRow>
        <Button
          className="w-full"
          label="Confirm Amount"
          onClick={handleSubmit}
          isLoading={isSubmitting}
        />
      </FormRow>
    </div>
  );
};

ConfirmMicroDepositPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
};

export default Validator(schema())(ConfirmMicroDepositPage);
